// App
// -------
//
// Note on network calls philosophy
// * Eventually this product will have customers with >1,000 web apps running tests each fortnight and in use for years
// * Each test may contain an average of 10 vulns
// * That means if we make 1 network call for each app, test and vuln we will make tens of thousands of network calls per user that logs in
// * The actual data may be only in the 10Mb's as images are links which are only accessed when required
// * To reduce this we:
//      1. Take advantage of the GraphQL 'list' calls to list web apps, tests, etc. Rather than making 1 call per web app
//      2. Call reduced test results lists, e.g. with no vulns
//

// React imports
import React, { useState, useEffect, useCallback, useRef } from "react";
//import { BrowserRouter as Router, Route, Redirect } from "react-router-dom";
import { BrowserRouter } from "react-router-dom";

// Graph and Auth
import { Amplify, Auth, Hub, Logger } from "aws-amplify";

// Class imports
import {
  ADMIN_GROUP,
  API_URL_PREFIX,
  API_URL_SUFFIX,
  BASEURL,
  CacheKey,
  COGNITO_MAP_SUFFIX,
  DASHBOARD_DISPLAYED_KEY,
  DISPLAY_DASHBOARD_ANIMATION,
  EMPTY_STRING,
  HTTPS,
  isTestLive,
  LIVE_TESTS_PHASE_VALUE,
  WWW_CLIENT,
} from "./constants/definitions";
import CognitoLoginClass from "./models/cognitoLogin";
import UserClass from "./models/user";
import {
  getListOfUsers,
  getListOfWebapps,
  getMinimalListOfWebapps,
  listLatestTestsForDashboardFromGraph,
  listVulnHistoriesFromGraph,
  listWebAppTestStatusesFromGraph,
} from "./services/dataListOperations.service";
import TestResultsClass from "./models/testResult";
import VulnerabilityClass from "./models/vulnerability";
import { createApolloClient } from "./services/graphql/createApolloClient";
import { mapLimit, scheduleIdle } from "./utils/concurrency";
import {
  eventBus,
  TEST_STATUS_UPDATE,
  VULN_STATUS_UPDATE,
  WEBAPP_TEST_STATUS_UPDATE,
  WEBAPP_UPDATE,
} from "./services/eventBus.service";

import WebApplicationClass, { LIVE_SIGNAL_SOURCE } from "./models/webApplication";
import { AppRoutes } from "./router";
import { AppProps } from "./types/interfaces";
import {
  cacheThenRefresh as cacheThenRefreshCached,
  getCache,
  purgeCache,
  setCache,
} from "./utils/cache";
import { warmTenantEntitlementsCache } from "./services/tenantEntitlements.service";
import { warmTenantUsageCache } from "./services/accountUsage.service";

const PRODUCTION_BUILD = process.env.NODE_ENV === "production";
const devClientName = process.env.REACT_APP_CLIENT_NAME || "";
const VALID_CLIENTNAME_CHARSET = /^[_a-zA-Z0-9]+$/;

// Backoff schedule for bringing the realtime (WebSocket) client up, in
// milliseconds; the last entry repeats for as long as the session lasts.
//
// The client heals itself from a connection it has already established,
// but a FIRST connect that fails has nothing to heal — and there is no
// louder symptom than the absence of one: no live test status, no
// findings arriving, no webapp changes, and nothing on screen saying so.
// The 60s ceiling is deliberate. A retry is cheap, a session with no
// realtime channel is not, and the session watchdog logs the user out
// within a minute if the cause was an expired session, which ends this
// loop on its own.
const REALTIME_CONNECT_RETRY_DELAYS_MS = [2_000, 5_000, 15_000, 30_000, 60_000];

// Dark auth-screen background (matches the `.dark` --background token and the auth
// pages' wrapper). Used to paint the boot screen dark for visitors heading to the
// dark Login page, so navigating in from the dark marketing site doesn't flash white.
const AUTH_DARK_BACKGROUND = "hsl(222, 70%, 8%)";

// Synchronously detect a stored Cognito session. Amplify persists auth tokens in
// localStorage under keys prefixed "CognitoIdentityServiceProvider.". A returning
// user (session present) lands on the light app; a fresh visitor (none) is bound
// for the dark Login page. Best-effort: any access error is treated as "no session"
// (the safe default for the sign-in entry point), so this never throws during boot.
function hasStoredCognitoSession(): boolean {
  try {
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index);
      if (key && key.indexOf("CognitoIdentityServiceProvider.") === 0) return true;
    }
  } catch {
    // localStorage unavailable (private mode / blocked) — treat as no session.
  }
  return false;
}

const App: React.FC<AppProps> = (props) => {
  // State (mirrors the old AppState interface)
  const [user, setUser] = useState<UserClass>(new UserClass());
  const [loggedIn, setLoggedIn] = useState(false);
  const [listOfWebapps, setListOfWebapps] = useState<WebApplicationClass[]>([]);
  const [currentWebAppId, setCurrentWebAppId] = useState("");
  const [dashboardListOfWebapps, setDashboardListOfWebapps] = useState<WebApplicationClass[] | null>(null);
  const [allUsers, setAllUsers] = useState<UserClass[]>([]);
  const [username, setUsernameState] = useState(EMPTY_STRING);
  const [oldpassword, setOldpassword] = useState(EMPTY_STRING);
  const [configError, setConfigError] = useState<string | null>(null);
  const [tier, setTier] = useState<"enterprise" | "sme">("enterprise");
  const [clientName, setClientName] = useState("");
  const [sessionRestoring, setSessionRestoring] = useState(true);
  // The realtime (WebSocket) client, once it has connected. State rather
  // than a ref alone because two things must react to its arrival: the
  // effect that wires up the per-webapp subscriptions, and the
  // WebApplication page, which takes it as a prop. A ref populated
  // asynchronously triggers neither.
  const [subscriptionClient, setSubscriptionClient] = useState<any>(null);
  // Set once the app is ready to bring the realtime client up. Separate
  // from loggedIn so the connect keeps exactly the trigger point it had
  // inside getUserDetails — after the endpoints are known.
  const [realtimeConnectRequested, setRealtimeConnectRequested] = useState(false);

  // Refs for mutable instance variables (equivalent to class properties)
  const cognitologinRef = useRef(new CognitoLoginClass());
  const regionRef = useRef<string | null>(null);
  const apiURLRef = useRef<string | null>(null);
  const realtimeEndpointRef = useRef<string | null>(null);
  const graphQLHostRef = useRef<string | null>(null);
  const subscriptionClientRef = useRef<any>(null);

  // Ref to track previous loggedIn value for componentDidUpdate-style logic
  const prevLoggedInRef = useRef(loggedIn);

  // Always-current view of listOfWebapps for code that must read the list
  // without depending on it (teardown paths, unmount cleanup). Assigned
  // during render so it is never a render behind.
  const listOfWebappsRef = useRef<WebApplicationClass[]>(listOfWebapps);
  listOfWebappsRef.current = listOfWebapps;

  // Unsubscribe From Vulns
  // -----------------------
  //
  const unsubscribeFromVulns = useCallback((webapps: WebApplicationClass[]) => {
    webapps.forEach((webapp) => {
      if (webapp?.tests?.length) {
        webapp.tests.forEach((test) => {
          if (test && typeof test.unsubscribeFromVulns === "function") {
            test.unsubscribeFromVulns();
          }
        });
      }
    });
  }, []);

  // Unsubscribe From All Live Updates
  // ----------------------------------
  // Tears down both the new vulns subscription (per-test) and the four
  // per-webapp live-update subscriptions (test status, webapp test status,
  // vuln status, webapp updates) so the WebSocket is fully drained before
  // the Cognito session is cleared on logout.
  const unsubscribeFromAllLiveUpdates = useCallback(
    (webapps: WebApplicationClass[]) => {
      unsubscribeFromVulns(webapps);
      webapps.forEach((webapp) => {
        if (webapp && typeof webapp.unsubscribeFromLiveUpdates === "function") {
          webapp.unsubscribeFromLiveUpdates();
        }
      });
    },
    [unsubscribeFromVulns]
  );

  // Subscribe To All Live Updates
  // ------------------------------
  // Wires the four per-webapp subscriptions for every webapp in the list.
  // Idempotent — safe to call repeatedly as the webapp list grows or as
  // a new webapp is added mid-session.
  //
  // Takes the client as an argument rather than reading the ref: the
  // caller is an effect that already knows the client is there, and
  // reading a ref would reintroduce the race this is here to close.
  const subscribeToAllLiveUpdates = useCallback(
    (webapps: WebApplicationClass[], connectedClient: any) => {
      if (!connectedClient) return;
      webapps.forEach((webapp) => {
        if (webapp && typeof webapp.subscribeToLiveUpdates === "function") {
          webapp.subscribeToLiveUpdates(connectedClient).catch((subscribeError) =>
            console.error(
              `Failed to subscribe to live updates for webapp ${webapp.id}:`,
              subscribeError
            )
          );
        }
      });
    },
    []
  );

  // Cache-then-refresh helper for dashboard preload batch calls.
  // ----------------------------------------------------------------
  // Applies a cached value immediately (if any, even if stale) so the dashboard
  // renders fast on repeat logins. Then fetches fresh data in the background
  // and re-applies it, refreshing the cache. Applier is called once per successful
  // application so a single setState can follow each call site.
  //
  // The implementation moved to utils/cache so surfaces outside this bootstrap
  // (the entitlements hook) read through the same strategy. Kept as a
  // useCallback wrapper so the preload callbacks below keep a stable dependency.
  //
  const cacheThenRefresh = useCallback(<T,>(
    cacheKey: CacheKey,
    fetcher: () => Promise<T>,
    apply: (data: T) => void
  ): Promise<void> => cacheThenRefreshCached(cacheKey, fetcher, apply), []);

  // Preload Web App Test Statuses
  // -------------------------------
  // Fetches every accessible webapp's test status in a single batch call,
  // then populates each webapp's webAppTestStatus model. One API call instead
  // of N. Triggers a single setState so the dashboard's summary counts, phase
  // chips, and risk scores all appear at once.
  //
  const preloadWebAppTestStatuses = useCallback(async (webapps: WebApplicationClass[]): Promise<void> => {
    const applyStatuses = (statuses: any[]) => {
      if (!Array.isArray(statuses) || statuses.length === 0) return;

      const statusById = new Map<string, any>();
      for (const entry of statuses) {
        if (entry?.id) {
          statusById.set(entry.id, entry);
        }
      }

      for (const webapp of webapps) {
        const data = statusById.get(webapp.id);
        if (data) {
          webapp.webAppTestStatus.populateFromData(data);
          // Note: do NOT overwrite webapp.testRunning here. Post the
          // FailedTestFindings BE deploy, the aggregate's currentPhase
          // is no longer used to signal "test in flight" — it carries
          // terminal phases only (No_Tests / No_Findings / Results_Ready
          // / Test_Failed). The in-flight signal lives on
          // webapp.inFlightTestStatus, populated by the dashboard
          // preload (listLatestTestsForDashboard). Reading currentPhase
          // here would clobber that with false for every app, since
          // LIVE_TESTS_PHASE_VALUE is never emitted any more.
        }
      }

      setListOfWebapps((prev) => [...prev]);
    };

    await cacheThenRefresh<any[]>(
      CacheKey.listWebAppTestStatuses,
      listWebAppTestStatusesFromGraph,
      applyStatuses
    );
  }, [cacheThenRefresh]);

  // Preload Latest Tests For Dashboard
  // ------------------------------------
  // Loads the latest test (with its vulns) for every accessible webapp in a
  // single batch call. Populates each webapp's `tests[0]` with a fully-formed
  // TestResultsClass containing lightweight VulnerabilityClass instances.
  // One GraphQL call instead of N sequential per-webapp calls.
  //
  // NOTE: The vuln summaries are minimal — enough for the dashboard's Open
  // Vulnerabilities table and category breakdown. Heavy fields like `render`,
  // `videoURL`, `stepsToReproduce`, and `recommendations` are fetched on demand
  // when the user navigates to an individual vuln page.
  //
  const preloadLatestTestsForDashboard = useCallback(async (webapps: WebApplicationClass[]): Promise<void> => {
    const applyTests = (testEntries: any[]) => {
      if (!Array.isArray(testEntries) || testEntries.length === 0) return;

      const testsByWebappId = new Map<string, any>();
      for (const entry of testEntries) {
        if (entry?.webappid) {
          testsByWebappId.set(entry.webappid, entry);
        }
      }

      for (const webapp of webapps) {
        const entry = testsByWebappId.get(webapp.id);
        if (!entry) continue;

        // Detect the BE response shape. Post-DashboardPreload_BEBrief
        // the BE returns the latest *terminal* test in the testid/vulns/
        // timestamp slots and ships the in-flight signal through
        // separate inFlightTestStatus / inFlightStartTime fields. The
        // presence of the property (not its truthiness) tells us which
        // shape we got — null is a legitimate "no in-flight test" value
        // from the new BE; undefined means a pre-deploy BE that hasn't
        // added the fields yet. During the transitional window we fall
        // back to deriving the live signal from testStatus so the
        // dashboard's Testing indicator keeps working.
        const hasInFlightShape =
          Object.prototype.hasOwnProperty.call(entry, "inFlightTestStatus");
        const inFlightStatus = hasInFlightShape
          ? (entry.inFlightTestStatus ?? null)
          : null;
        const inFlightStart = hasInFlightShape
          ? (entry.inFlightStartTime ?? null)
          : null;

        // Funnel the live-run signal through the single setter
        // (setTestActivity), whichever BE shape we got:
        //  - New shape (hasInFlightShape): trust the dedicated
        //    inFlightTestStatus field. Null/terminal → no live run
        //    (the setter clears). The in-flight test's own id isn't
        //    carried by the preload, so testId stays null; the next
        //    subscription/resync fills it.
        //  - Legacy shape: derive from the relevant test instance's own
        //    live state (it can itself be the in-flight test).
        // Note this preload is read through cacheThenRefresh, so applyTests
        // runs twice: once on a cached entry that can be arbitrarily old,
        // then on the fresh one. The cached pass can therefore offer a
        // phase from a previous visit; setTestActivity's monotonic rule is
        // what stops it rewinding a run that has since moved on.
        const applyLiveSignal = (legacyCandidate: TestResultsClass | null) => {
          if (hasInFlightShape) {
            webapp.setTestActivity(
              inFlightStatus
                ? {
                    testId: null,
                    testStatus: inFlightStatus,
                    startTime: inFlightStart ?? null,
                  }
                : null,
              { source: LIVE_SIGNAL_SOURCE.dashboardPreload },
            );
          } else if (legacyCandidate && legacyCandidate.isLive) {
            webapp.setTestActivity(
              {
                testId: legacyCandidate.id || null,
                testStatus: legacyCandidate.testStatus ?? "",
                startTime: legacyCandidate.startTime
                  ? Number(legacyCandidate.startTime)
                  : null,
              },
              { source: LIVE_SIGNAL_SOURCE.dashboardPreload },
            );
          } else {
            webapp.setTestActivity(null, {
              source: LIVE_SIGNAL_SOURCE.dashboardPreload,
            });
          }
        };

        const existing = webapp.tests[0];

        // If a live test is already in flight on this webapp, the
        // websocket subscriptions are the source of truth — don't let
        // the dashboard preload's slim view replace the in-memory
        // instance. Replacing would orphan it (subscription handles,
        // testSelected reference, vulns received since the preload was
        // issued) and the next onTestStatusChange / onNewVulnerability
        // event would mutate a different instance than the one the UI
        // is rendering. Backfill the placeholder id if we don't have
        // one yet (webapp.startTest was still awaiting the backend
        // when this preload landed) so subsequent matches by testid
        // still hit this instance.
        if (existing && existing.isLive) {
          if (!existing.id && entry.testid) {
            existing.id = entry.testid;
          }
          // The in-flight instance is `existing`; funnel it (or the BE's
          // dedicated signal) through the single setter.
          applyLiveSignal(existing);
          continue;
        }

        // Post-DashboardPreload_BEBrief, the BE may legitimately return
        // an entry with no testid — the app has no terminal test yet,
        // only an in-flight one (or none at all). Surface the in-flight
        // signal on the webapp and skip the test-instance creation;
        // webapp.tests stays empty (or unchanged from prior state).
        if (!entry.testid) {
          // No terminal test instance in this branch; the live signal
          // comes solely from the BE's dedicated field (or is cleared).
          applyLiveSignal(null);
          continue;
        }

        // Same non-live test as we're already tracking — update slim
        // fields in place rather than replacing the instance.
        //
        // For vulns: REPLACE rather than merge. The earlier-deployed
        // "merge new IDs, keep local-only" logic accumulated stale
        // vulns indefinitely — once a record made it into the local
        // model, no BE refresh could remove it, even if the BE later
        // deleted the record (e.g. after a dedup cleanup). The merge
        // was originally written to protect subscription-pushed vulns
        // from being clobbered by a slower refresh; that protection
        // only matters while a test is live, and the existing.isLive
        // guard above already short-circuits in that case. By the
        // time we reach this branch the test is non-live, BE is
        // authoritative, and replacing is safe.
        if (existing && existing.id === entry.testid) {
          existing.startTime = entry.startTime ?? existing.startTime;
          existing.endTime = entry.endTime ?? existing.endTime;
          existing.endTrigger = entry.endTrigger ?? existing.endTrigger;
          existing.testStatus = entry.testStatus ?? existing.testStatus;
          existing.isLive = isTestLive(existing.testStatus, existing.endTime);
          // Funnel the live signal through the single setter — the BE's
          // dedicated field (new shape) or this instance's own live state
          // (legacy shape).
          applyLiveSignal(existing);
          // Slim DashboardVulnSummary projection — only overwrite
          // the vulns array when the existing record hasn't been
          // upgraded to a full hydration yet. If selectTestById /
          // getFullTestResult already ran (fullResultLoaded === true),
          // its vulns carry fields the dashboard preload doesn't
          // project — render, videoURL, steps, recommendations,
          // subFindings, etc — and we don't want to
          // clobber them with the slim shape. The
          // fullResultLoaded=true ALSO suppresses re-fetch on the
          // next webapp-page mount, so once clobbered the FE has no
          // recovery path without a logout.
          if (Array.isArray(entry.vulns) && !existing.fullResultLoaded) {
            existing.vulns = entry.vulns
              .filter((vulnDict: any) => !!vulnDict?.id)
              .map((vulnDict: any) => {
                const vuln = new VulnerabilityClass();
                vuln.setVulnerability(vulnDict);
                return vuln;
              });
          }
          continue;
        }

        const testInstance = new TestResultsClass();
        testInstance.id = entry.testid;
        testInstance.startTime = entry.startTime ?? "";
        testInstance.endTime = entry.endTime ?? "";
        // endTrigger and testStatus carry the cancelled / invalid / completed
        // signal that the dashboard filters and the report wording both depend
        // on. Without these, every preloaded test looks like a clean
        // completion regardless of how it actually ended.
        testInstance.endTrigger = entry.endTrigger ?? "";
        testInstance.testStatus = entry.testStatus ?? null;
        testInstance.isLive = isTestLive(entry.testStatus, entry.endTime);
        testInstance.fullResultLoaded = false;

        if (Array.isArray(entry.vulns)) {
          for (const vulnDict of entry.vulns) {
            const vuln = new VulnerabilityClass();
            vuln.setVulnerability(vulnDict);
            testInstance.vulns.push(vuln);
          }
        }

        webapp.tests = [testInstance];
        webapp.testCount = 1;
        // Recover the live signal through the single setter. New shape:
        // trust the dedicated in-flight field (testInstance here is the
        // latest terminal test, so its isLive is false even when a
        // separate live run is underway). Legacy shape: derive from the
        // instance's own live state (it can itself be in-flight). Without
        // this, a logout/login mid-test would suppress the indicator.
        applyLiveSignal(testInstance);
      }

      setListOfWebapps((prev) => [...prev]);
    };

    await cacheThenRefresh<any[]>(
      CacheKey.listLatestTestsForDashboard,
      listLatestTestsForDashboardFromGraph,
      applyTests
    );
  }, [cacheThenRefresh]);

  // Preload Vuln History
  // ----------------------
  // Fetches all vuln histories in a single batch call, then populates each
  // webapp's vulnHistory model from the result. One API call instead of N.
  //
  const preloadVulnHistory = useCallback(async (webapps: WebApplicationClass[]): Promise<void> => {
    const applyHistories = (histories: any[]) => {
      if (!Array.isArray(histories) || histories.length === 0) return;

      const historyById = new Map<string, { highs: number[]; meds: number[]; lows: number[] }>();
      for (const entry of histories) {
        if (entry?.id) {
          historyById.set(entry.id, entry);
        }
      }

      for (const webapp of webapps) {
        const data = historyById.get(webapp.id);
        if (data) {
          webapp.vulnHistory.populateFromData(data);
        }
      }

      setListOfWebapps((prev) => [...prev]);
    };

    await cacheThenRefresh<any[]>(
      CacheKey.listVulnHistories,
      listVulnHistoriesFromGraph,
      applyHistories
    );
  }, [cacheThenRefresh]);

  // Eager-hydrate full test results
  // --------------------------------
  // After the dashboard preload finishes, walk every webapp and fetch
  // the full `getTestResults` payload (vulns inline with their
  // stepsToReproduce / recommendations / details / render) for its
  // latest test. This trades a few hundred milliseconds of background
  // network at login for near-instant Vulnerability-page loads later:
  // by the time the user drills into any vuln from the dashboard, the
  // full test record is already on the model and the on-demand
  // selectTestById fetch is a no-op (fullResultLoaded is true).
  //
  // Concurrency capped at 4 to keep AppSync happy and to leave room
  // for any user-initiated request that fires during the warmup.
  // Skips tests that have already been hydrated (either because the
  // user navigated fast enough to trigger an on-demand fetch, or
  // because this function ran in a previous bootstrap).
  const eagerHydrateLatestTests = useCallback(
    async (webapps: WebApplicationClass[]): Promise<void> => {
      const candidates: TestResultsClass[] = [];
      for (const webapp of webapps) {
        const latest = webapp.tests?.[0];
        if (latest && latest.id && !latest.fullResultLoaded) {
          candidates.push(latest);
        }
      }
      if (candidates.length === 0) return;

      await mapLimit(candidates, 4, async (test) => {
        try {
          await test.getFullTestResult(test.id);
        } catch {
          // Per-test failures are non-fatal — the on-demand fetch in
          // the Vulnerability page remains the fallback path.
        }
      });
    },
    []
  );

  // Fetch Dashboard List of Webapps
  // --------------------------------
  // Fetches the app list first (required for the dashboard to render at all),
  // then awaits the three enrichment queries (severity counts, open vulns,
  // sparklines) so the dashboard is fully populated before the login bloom
  // overlay fades. The 1.6s bloom animation provides visual cover while these
  // run, so the user never stares at a blank screen. A safety timeout prevents
  // a slow or hung query from blocking indefinitely.
  const fetchDashboardListOfWebapps = useCallback(async () => {
    const cacheDashboardListOfWebapps = await getCache<WebApplicationClass[]>(
      CacheKey.quickListWebApplications
    );

    if (cacheDashboardListOfWebapps?.value) {
      setDashboardListOfWebapps(cacheDashboardListOfWebapps.value);
    }

    const value = await getMinimalListOfWebapps();
    setCache(CacheKey.quickListWebApplications, value);

    // Every call here builds a BRAND NEW WebApplicationClass per app. The
    // instances being replaced may still hold live subscriptions and a
    // running in-flight resync timer, and nothing else will ever tear them
    // down: the logout path drains whatever list is current at that moment,
    // which by then is this new one. Left alone they keep polling and keep
    // receiving events into models no surface renders — while the models the
    // UI does render sit frozen on whatever phase they were built with.
    unsubscribeFromAllLiveUpdates(listOfWebappsRef.current);

    const webapps = getListOfWebapps(value || [], subscriptionClientRef.current);
    setDashboardListOfWebapps(value);
    setListOfWebapps(webapps);

    // The four per-webapp live-update subscriptions are NOT wired here.
    // They need both this list and a connected realtime client, and the
    // two arrive independently — subscribing from this point silently did
    // nothing whenever the WebSocket handshake lost the race with the app
    // list, which cost the session every live update with no error to
    // show for it. The effect that owns that pairing runs when both are
    // present, however they turn up.

    // Await all three enrichment queries so the dashboard is fully populated
    // before we navigate. The bloom animation (1.6s) runs in parallel, giving
    // these queries plenty of time. A safety timeout ensures we don't hang
    // if a query is abnormally slow.
    const ENRICHMENT_SAFETY_TIMEOUT_MS = 3000;
    const enrichmentQueries = Promise.allSettled([
      preloadWebAppTestStatuses(webapps),
      preloadLatestTestsForDashboard(webapps),
      preloadVulnHistory(webapps),
    ]);
    const safetyTimeout = new Promise<void>((resolve) =>
      setTimeout(resolve, ENRICHMENT_SAFETY_TIMEOUT_MS)
    );
    await Promise.race([enrichmentQueries, safetyTimeout]);

    // Fire-and-forget warm-ups, scheduled at idle so they don't compete with
    // the dashboard's first paint or the user's first interactions, and not
    // awaited so the bootstrap completes (and the dashboard navigates) without
    // waiting for them. Nothing on the dashboard depends on either.
    //
    // Ordered cheapest-and-soonest-needed first: license slots are a single
    // small query and the first thing the Add-App page blocks on, so warming
    // them ahead of the (much heavier) per-test hydration turns that page's
    // visible wait into a cache read. Not chained — a slow or failed warm-up
    // must not hold up hydration.
    scheduleIdle(() => {
      warmTenantEntitlementsCache();
      // Same rationale, same cost profile: one small query whose absence is
      // visible as a self-correcting seat count on the Users page.
      warmTenantUsageCache();

      // Eager hydration of every webapp's latest test: by the time the user
      // clicks into any app or vuln, the full test record is already on the
      // model.
      eagerHydrateLatestTests(webapps).catch(() => {
        // mapLimit swallows per-task errors already; this catch is a
        // belt-and-suspenders for the outer promise.
      });
    });
  }, [
    preloadWebAppTestStatuses,
    preloadLatestTestsForDashboard,
    preloadVulnHistory,
    eagerHydrateLatestTests,
    unsubscribeFromAllLiveUpdates,
  ]);

  // Fetch User
  const fetchUser = useCallback(async () => {
    const userInstance = new UserClass();

    const cachedUser = await getCache(CacheKey.getUser);

    if (cachedUser?.value) {
      userInstance.setUser(cachedUser.value);
      setUser(userInstance);
    }

    const value = await userInstance.getUser();
    setCache(CacheKey.getUser, value);
    setUser(userInstance);
    setLoggedIn(true);

    getListOfUsers(userInstance).then((fetchedUsers) => {
      setAllUsers(fetchedUsers);
    });
  }, []);

  // Get User Details
  // -----------------
  // Call to get the authenticated user details
  // And the web apps the user has access to
  //
  const getUserDetails = useCallback(async (): Promise<void> => {
    // Ask for the realtime client. Deliberately just a flag: the connect
    // itself, its retries and its teardown are owned by an effect, so a
    // failure has somewhere to be retried from rather than dying inside a
    // fire-and-forget promise. Nothing here waits on it — the dashboard
    // doesn't use subscriptions on its first render.
    setRealtimeConnectRequested(true);

    // fetchUser is intentionally fire-and-forget so the dashboard doesn't
    // block on it. A .catch() is required because it makes API.graphql()
    // calls internally: if the user logs out while these are in-flight,
    // Auth.signOut() clears the Cognito session and API.graphql() throws
    // "No current user" — without .catch(), that becomes an unhandled
    // Promise rejection that surfaces as a React error overlay.
    fetchUser().catch(() => {});

    // Read the tier and tenant identity from the Cognito token (immutable,
    // server-signed). These are local JWT reads — essentially instant.
    const fetchedTier = await cognitologinRef.current.getTier();
    setTier(fetchedTier);

    const tenantId = await cognitologinRef.current.getTenantId();
    if (tenantId) {
      const displayName = tenantId
        .replace(/[-_]/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());
      setClientName(displayName);
    }

    // The only blocking call: fetch the app list. This is what the dashboard
    // needs to render its first useful frame. The three enrichment queries
    // (severity counts, open vulns, sparklines) are kicked off inside
    // fetchDashboardListOfWebapps as fire-and-forget.
    await fetchDashboardListOfWebapps();
  }, [fetchUser, fetchDashboardListOfWebapps]);

  // Restore Session
  // ----------------
  // If the user has a valid Cognito session (tokens in localStorage), restore their
  // authenticated state without requiring re-login. This handles page refreshes.
  //
  const restoreSession = useCallback(async (): Promise<void> => {
    try {
      await Auth.currentSession();
      // Session is valid — mark as authenticated and load user data
      cognitologinRef.current.markAsAuthenticated();
      await getUserDetails();
    } catch {
      // No valid session — user will need to log in
    } finally {
      setSessionRestoring(false);
    }
  }, [getUserDetails]);

  // Set Graph Details
  // ------------------
  // Config information here
  // https://aws-amplify.github.io/docs/js/api
  // https://aws-amplify.github.io/docs/js/api#amplify-graphql-client
  //
  const setGraphDetails = useCallback(async (
    apiURL: string,
    region: string,
    realtimeEndpoint: string,
    graphQLHost: string
  ): Promise<void> => {
    // Create the client for graphql mutation/query.
    //
    // graphql_headers is required: with AMAZON_COGNITO_USER_POOLS auth Amplify
    // attaches the access token by default, which lacks the custom:tenantId
    // and custom:tier claims that tenant-scoped resolvers depend on. Sending
    // the id token explicitly mirrors the claim shape the resolvers expect
    // (and the dashboard preload, which works, also runs on the id token).
    const myAppConfig = {
      aws_appsync_graphqlEndpoint: apiURL,
      aws_appsync_region: region,
      aws_appsync_authenticationType: "AMAZON_COGNITO_USER_POOLS", // Before calling API.graphql(...) is required to do Auth.federatedSignIn(...) check authentication guide for details.
      graphql_headers: async () => {
        const session = await Auth.currentSession();
        return { Authorization: session.getIdToken().getJwtToken() };
      },
    };

    Amplify.configure(myAppConfig);

    // Preflight: fire a throwaway, unauthenticated request at the AppSync
    // endpoint so the browser has DNS resolved and a TLS session open before
    // the first real call after login. AppSync answers 401 at its auth check,
    // which is expected and ignored — so this warms the CONNECTION only: no
    // resolver or Lambda runs. The login-path Lambdas are kept warm
    // server-side by an EventBridge schedule (see models/lambda_warmup.py in
    // nimis-intelligence-backend).
    fetch(apiURL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ query: "{ __typename }" }),
    }).catch(() => {});

    regionRef.current = region;
    apiURLRef.current = apiURL;
    realtimeEndpointRef.current = realtimeEndpoint;
    graphQLHostRef.current = graphQLHost;

    // Attempt to restore an existing Cognito session on page refresh
    await restoreSession();
  }, [restoreSession]);

  // Get Cognito Map
  const getCognitoMap = useCallback(async (cognitomapURL: string, apiurl: string) => {
    const MAX_RETRIES = 2;
    const RETRY_DELAY_MS = 1000;
    const REQUIRED_FIELDS = ["region", "uuid", "ref", "key", "hash", "clientname"];

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        const response = await fetch(cognitomapURL);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status} fetching configuration`);
        }

        const data = await response.json();

        // Validate all required fields are present and non-empty
        for (const field of REQUIRED_FIELDS) {
          if (!data[field] || typeof data[field] !== "string") {
            throw new Error(`Invalid configuration: missing or empty field '${field}'`);
          }
        }

        const region = data.region;
        const appclientid = data.key;
        const userpoolid = region + "_" + data.ref;
        // Identity pool ids are region-prefixed; map.json carries only the UUID
        // half (see infra create_map_file.py). Used ONLY for guest credentials on
        // the unauthenticated requestNewInvite recovery call.
        const identityPoolId = region + ":" + data.uuid;
        const realtimeEndpoint =
          "wss://" + data.hash + ".appsync-realtime-api." + region + ".amazonaws.com/graphql";
        const graphQLHost =
          data.hash + ".appsync-api." + region + ".amazonaws.com";

        cognitologinRef.current.configure(region, userpoolid, appclientid, identityPoolId);
        await setGraphDetails(apiurl, region, realtimeEndpoint, graphQLHost);
        return;

      } catch (error: any) {
        if (attempt < MAX_RETRIES) {
          await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
        } else {
          console.error("Failed to load configuration after retries:", error);
          setConfigError("Unable to load application configuration. Please try refreshing the page.");
        }
      }
    }
  }, [setGraphDetails]);

  /******************************************/
  /*                Callbacks               */
  /******************************************/

  // Selected Web App Callback
  // --------------------------
  // Here we change the current webappid
  const updateCurrentWebAppCallback = useCallback((webappid: string): void => {
    setCurrentWebAppId(webappid);
  }, []);

  // Change User to Admin Callback
  // -------------------------------
  //
  const changeUserToAdminCallback = useCallback((): void => {
    setUser((prevUser) => {
      const updatedUser = Object.assign(
        Object.create(Object.getPrototypeOf(prevUser)),
        prevUser
      );
      updatedUser.role = [ADMIN_GROUP];
      return updatedUser;
    });
  }, []);

  // Update List of Apps Callback
  // -----------------------------
  //
  const updateListOfAppsCallback = useCallback((updatedList: any[]): void => {
    setListOfWebapps(updatedList);
  }, []);

  // Verify App
  // -----------
  // An app has been verified, update the state
  //
  const verifyApp = useCallback((webappid: string): void => {
    setListOfWebapps((prev) =>
      prev.map((app) => {
        if (app.id === webappid) {
          app.ownershipVerified = true;
        }
        return app;
      })
    );
  }, []);

  // Update Web App Status
  // ----------------------
  // Used to keep the status of the web app in sync with the back end without a periodic refresh
  // If the vuln status or risk changes to the most recent test then update the web app status
  //
  const updateWebAppStatus = useCallback((
    webappid: string,
    testresultid: string,
    oldRisk: string,
    newRisk: string,
    oldStatus: string,
    newStatus: string
  ): void => {
    setListOfWebapps((prev) => {
      for (let i = 0; i < prev.length; i++) {
        const app = prev[i];
        if (app.id === webappid) {
          // If there are tests then get the last test id
          // If this matches the current test
          if (
            app.getLastCurrentTestId &&
            app.getLastCurrentTestId() === testresultid
          ) {
            // Update the test status
            if (
              app.webAppTestStatus &&
              app.webAppTestStatus.updateWebAppTestStatus
            ) {
              app.webAppTestStatus.updateWebAppTestStatus(
                oldRisk,
                newRisk,
                oldStatus,
                newStatus
              );
            }
          }
          break;
        }
      }
      return [...prev];
    });
  }, []);

  // Set the Username
  // -----------------
  // Used by Activate User page, set from Welcome/Login
  //
  const setUsername = useCallback((usernameValue: string, oldpasswordValue: string): void => {
    setUsernameState(usernameValue);
    setOldpassword(oldpasswordValue);
  }, []);

  // Record the button-click timestamp for the webapp. The running signal
  // itself now lives in webapp.testActivity (set by startTest / stopTest /
  // the subscription handler), so this only refreshes the timestamp and
  // re-renders the list.
  const updateWebAppTestState = useCallback((
    webappid: string,
    buttonClickTime: Date
  ): void => {
    setListOfWebapps((prev) => {
      for (let i = 0; i < prev.length; i++) {
        const app = prev[i];
        if (app.id === webappid) {
          if (app.updateWebAppTestStatus) {
            app.updateWebAppTestStatus(buttonClickTime);
          }
          break;
        }
      }
      return [...prev];
    });
  }, []);

  /******************************************/
  /*              Effects                   */
  /******************************************/

  // componentDidMount equivalent — load config
  useEffect(() => {
    let pageurl = window.location.href;
    pageurl = pageurl.replace(HTTPS, EMPTY_STRING);

    let clientname = pageurl.split(".")[0];
    if (!PRODUCTION_BUILD) {
      clientname = devClientName;
    }

    if (
      clientname &&
      WWW_CLIENT !== clientname &&
      clientname.length > 0 &&
      clientname.length < 1000 &&
      VALID_CLIENTNAME_CHARSET.test(clientname)
    ) {
      const clienturl = HTTPS + clientname + BASEURL;
      const cognitomapURL = clienturl + COGNITO_MAP_SUFFIX;

      const apiurl =
        HTTPS + API_URL_PREFIX + clientname + BASEURL + API_URL_SUFFIX;

      getCognitoMap(cognitomapURL, apiurl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hub.listen for Cognito auth events (with cleanup)
  useEffect(() => {
    const authListener: any = new Logger("");
    authListener.onHubCapsule = (capsule: any) => {
      switch (capsule.payload.event) {
        case "signIn":
          // Used to determine if the dashboard has been displayed once per log in, this is updated in the dashbaord component
          localStorage.setItem(
            DASHBOARD_DISPLAYED_KEY,
            DISPLAY_DASHBOARD_ANIMATION
          );
          break;
        case "signUp":
          break;
        case "signOut":
          setUser(new UserClass());
          setLoggedIn(false);

          if (subscriptionClientRef.current?.disconnect) {
            subscriptionClientRef.current.disconnect();
            subscriptionClientRef.current = null;
          }
          setSubscriptionClient(null);
          // Stops the bring-up effect: its cleanup cancels any retry
          // still pending, so a signed-out session doesn't sit there
          // knocking on the WebSocket once a minute.
          setRealtimeConnectRequested(false);

          purgeCache();
          break;
        case "signIn_failure":
          break;
        case "configured":
          break;
        default:
          // nothing to do
          break;
      }
    };

    Hub.listen("auth", authListener);

    return () => {
      Hub.remove("auth", authListener);
    };
  }, []);

  // Realtime client bring-up
  // -------------------------
  // Owns the connect, its retries and its teardown.
  //
  // This used to be a fire-and-forget createApolloClient inside
  // getUserDetails whose .catch() logged and set the ref to null. That
  // made a single failed handshake permanent: the client instance was
  // discarded, nothing re-attempted it, and the session ran to its end
  // with no live test status, no findings and no webapp updates —
  // silently, because every consumer treats a missing client as "not
  // ready yet" rather than as an error.
  useEffect(() => {
    if (!realtimeConnectRequested) return undefined;

    let attemptCancelled = false;
    let retryTimerId: ReturnType<typeof setTimeout> | null = null;
    let attemptCount = 0;

    const attemptConnect = async (): Promise<void> => {
      if (attemptCancelled) return;

      const graphqlEndpoint = apiURLRef.current;
      const realtimeEndpoint = realtimeEndpointRef.current;
      const graphQLHost = graphQLHostRef.current;
      // Requested before the config landed — setGraphDetails populates
      // these refs and only then triggers the request, so this is a
      // guard rather than an expected path.
      if (!graphqlEndpoint || !realtimeEndpoint || !graphQLHost) return;

      try {
        const client = await createApolloClient({
          graphqlEndpoint,
          realtimeEndpoint,
          graphQLHost,
        });
        if (attemptCancelled) {
          // Logged out (or unmounted) while the handshake was in flight.
          client.disconnect();
          return;
        }
        subscriptionClientRef.current = client;
        setSubscriptionClient(client);
      } catch (connectError) {
        if (attemptCancelled) return;
        const retryDelayMs =
          REALTIME_CONNECT_RETRY_DELAYS_MS[
            Math.min(attemptCount, REALTIME_CONNECT_RETRY_DELAYS_MS.length - 1)
          ];
        attemptCount += 1;
        console.error(
          `Realtime client unavailable, retrying in ${retryDelayMs}ms:`,
          connectError
        );
        retryTimerId = setTimeout(attemptConnect, retryDelayMs);
      }
    };

    attemptConnect();

    return () => {
      attemptCancelled = true;
      if (retryTimerId) clearTimeout(retryTimerId);
    };
  }, [realtimeConnectRequested]);

  // Pair the realtime client with the webapp list
  // ---------------------------------------------
  // The four per-webapp subscriptions need both, and the two arrive
  // independently: the app list from a query, the client from a
  // WebSocket handshake. Whichever is second triggers this.
  //
  // Keyed on the ARRAY, i.e. on the identity of the model instances in it —
  // not, as it used to be, on a join of their ids.
  //
  // The ids are the same string before and after the dashboard preload
  // rebuilds the list, but every WebApplicationClass in it is a new object.
  // Keyed on ids, this effect did not re-run for that rebuild, so the fresh
  // instances — the ones every page renders — were never subscribed and never
  // started their in-flight resync, while the discarded ones went on
  // receiving events invisibly. A run started from such a page showed
  // "Starting…" for its entire duration: the click wrote Provisioning onto a
  // model that nothing would ever advance, and only a re-login (which
  // re-seeds the signal from the backend) put the button right.
  //
  // Re-running on the live-event array bump is the cost of keying this way.
  // It is a per-webapp field check that returns before any await — no network,
  // no allocation of note — because subscribeToLiveUpdates is idempotent.
  useEffect(() => {
    if (!subscriptionClient) return;
    subscribeToAllLiveUpdates(listOfWebapps, subscriptionClient);
  }, [subscriptionClient, listOfWebapps, subscribeToAllLiveUpdates]);

  // componentDidUpdate equivalent — detect logout and unsubscribe
  useEffect(() => {
    if (prevLoggedInRef.current && !loggedIn) {
      unsubscribeFromAllLiveUpdates(listOfWebapps);
    }
    prevLoggedInRef.current = loggedIn;
  }, [loggedIn, listOfWebapps, unsubscribeFromAllLiveUpdates]);

  // Live-update bump
  // ----------------
  // Per-webapp subscriptions mutate model fields in place. React doesn't see
  // the mutation on its own. Bumping the listOfWebapps array reference here
  // re-renders every consumer (Dashboard, WebApplication page, etc.) so the
  // updated model values reach the screen. Centralised at the App level so
  // a single bump cascades through the tree instead of every page wiring
  // its own listener.
  useEffect(() => {
    const bumpWebappsArray = () => setListOfWebapps((prev) => [...prev]);
    eventBus.on(TEST_STATUS_UPDATE, bumpWebappsArray);
    eventBus.on(WEBAPP_TEST_STATUS_UPDATE, bumpWebappsArray);
    eventBus.on(VULN_STATUS_UPDATE, bumpWebappsArray);
    eventBus.on(WEBAPP_UPDATE, bumpWebappsArray);
    return () => {
      eventBus.off(TEST_STATUS_UPDATE, bumpWebappsArray);
      eventBus.off(WEBAPP_TEST_STATUS_UPDATE, bumpWebappsArray);
      eventBus.off(VULN_STATUS_UPDATE, bumpWebappsArray);
      eventBus.off(WEBAPP_UPDATE, bumpWebappsArray);
    };
  }, []);

  // componentWillUnmount equivalent — cleanup subscriptions.
  // Reads listOfWebappsRef (assigned every render, declared with the other
  // refs above) so the cleanup sees the current list without this effect
  // having to re-run for every change to it.

  useEffect(() => {
    return () => {
      unsubscribeFromAllLiveUpdates(listOfWebappsRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /******************************************/
  /*                  Render                */
  /******************************************/

  // Render
  // -------
  // Note: Login page will get the graphql details when unauthenticated. This is okay - not hiding anything here.
  // Note: There used to be an authentication check here, however this resulted in remounting the entire page when combined with Switch and CSSTransition
  //       As a result this is now moved to the individual components
  //
  if (sessionRestoring) {
    // Paint the boot screen to match where the user is headed so navigating in from
    // the dark marketing site doesn't flash white before the dark Login page paints.
    // No stored session -> bound for the dark Login page -> paint dark; otherwise the
    // user lands on the light app, so keep the boot light (no regression).
    const headingToLogin = !hasStoredCognitoSession();
    return (
      <div
        data-theme={headingToLogin ? "dark" : undefined}
        className="flex items-center justify-center min-h-screen"
        style={{ backgroundColor: headingToLogin ? AUTH_DARK_BACKGROUND : "#ffffff" }}
      >
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
      </div>
    );
  }

  if (configError) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", backgroundColor: "#1a1a2e", color: "#e0e0e0", fontFamily: "sans-serif" }}>
        <div style={{ textAlign: "center", maxWidth: "500px", padding: "40px" }}>
          <h2 style={{ color: "#ff6b6b", marginBottom: "16px" }}>Configuration Error</h2>
          <p>{configError}</p>
          <button onClick={() => window.location.reload()} style={{ marginTop: "20px", padding: "10px 24px", backgroundColor: "#4a4a8a", color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer" }}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <div id="appContainer">
        <div className="relative min-h-screen">
          <AppRoutes
            cognitologin={cognitologinRef.current}
            getUserDetails={getUserDetails}
            setGraphDetails={setGraphDetails}
            setUsername={setUsername}
            username={username}
            oldpassword={oldpassword}
            user={user}
            allUsers={allUsers}
            listOfWebapps={listOfWebapps}
            dashboardListOfWebapps={dashboardListOfWebapps}
            updateCurrentWebAppCallback={updateCurrentWebAppCallback}
            changeUserToAdminCallback={props.changeUserToAdminCallback}
            updateListOfAppsCallback={updateListOfAppsCallback}
            verifyApp={verifyApp}
            currentWebAppId={currentWebAppId}
            updateWebAppStatus={updateWebAppStatus}
            updateWebAppTestState={updateWebAppTestState}
            subscriptionClient={subscriptionClient}
            tier={tier}
            clientName={clientName}
          />
        </div>
      </div>
    </BrowserRouter>
  );
};

export default App;
